'use client'

import { useRef } from 'react'
import Image from 'next/image'
import { Link } from '@/i18n/navigation'
import type { Flux3TestPage } from '@/lib/flux3/tests.generated'

/**
 * Index card. The poster carries first paint; the clip only loads and plays on
 * hover, so a page of thirteen cards doesn't pull thirteen videos on load.
 */
export function TestCard({ test, size = 'default' }: { test: Flux3TestPage; size?: 'default' | 'wide' }) {
  const video = useRef<HTMLVideoElement>(null)

  const play = () => {
    const v = video.current
    if (!v) return
    if (!v.src) v.src = test.hero?.video ?? ''
    v.play().catch(() => {})
  }
  const stop = () => {
    const v = video.current
    if (!v) return
    v.pause()
    v.currentTime = 0
  }

  return (
    <Link
      href={`/models/flux-3/reports/${test.slug}`}
      onMouseEnter={play}
      onMouseLeave={stop}
      onFocus={play}
      onBlur={stop}
      className="group block"
    >
      <div className="relative aspect-[16/10] overflow-hidden rounded-xl bg-white/[0.03]">
        {test.hero?.poster ? (
          <Image
            src={test.hero.poster}
            alt=""
            fill
            unoptimized
            sizes="(min-width: 768px) 50vw, 100vw"
            className="object-cover opacity-80 transition-opacity duration-500 group-hover:opacity-0"
          />
        ) : null}
        <video
          ref={video}
          poster={test.hero?.poster ?? undefined}
          muted
          loop
          playsInline
          preload="none"
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        />
        <span className="absolute bottom-3 end-3 rounded-md bg-black/70 px-2 py-1 font-mono text-[11px] leading-none text-white/80 backdrop-blur-sm">
          {test.takeCount} takes
        </span>
      </div>

      <h3
        className={`mt-4 font-semibold tracking-tight text-white ${size === 'wide' ? 'text-2xl md:text-3xl' : 'text-xl'}`}
      >
        {test.title}
      </h3>
      <p className="mt-1.5 font-mono text-xs text-white/40">{test.question}</p>
      {test.lead ? (
        <p className="mt-3 text-[15px] leading-relaxed text-white/60 transition-colors group-hover:text-white/80">
          {test.lead}
        </p>
      ) : null}
    </Link>
  )
}
